import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Star, Truck, MapPin, Phone, Calendar, Award, Heart } from "lucide-react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import RatingReply from "../components/RatingReply";
import RatingDetails from "../components/RatingDetails";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierProfile() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favBusy, setFavBusy] = useState(false);

  const isFavorite = user?.favorite_suppliers?.includes(id);
  const canFav = user?.role === "restaurant";

  const toggleFav = async () => {
    setFavBusy(true);
    try {
      if (isFavorite) {
        await api.delete(`/restaurant/favorites/${id}`);
        toast.success("Rimosso dai preferiti");
      } else {
        await api.post(`/restaurant/favorites/${id}`);
        toast.success("Aggiunto ai preferiti");
      }
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setFavBusy(false); }
  };

  useEffect(() => {
    let m = true;
    axios.get(`${API}/suppliers/${id}`)
      .then(({ data }) => { if (m) setSupplier(data); })
      .catch(e => { if (m) setError(e.response?.data?.detail || "Fornitore non trovato"); })
      .finally(() => { if (m) setLoading(false); });
    return () => { m = false; };
  }, [id]);

  if (loading) return <div className="mx-auto max-w-3xl px-4 py-16 text-slate-500">Caricamento...</div>;
  if (error || !supplier) return (
    <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <div className="text-slate-500 text-sm">{error || "Non trovato"}</div>
      <Link to="/" className="mt-4 inline-block"><Button variant="outline" className="rounded-full">Torna alla home</Button></Link>
    </div>
  );

  const stars = supplier.rating_avg || 0;
  const fullStars = Math.round(stars);

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
      <div className="ga-card p-8">
        <div className="flex flex-col sm:flex-row sm:items-start gap-6">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-display font-bold shrink-0" style={{background: "linear-gradient(135deg,#C85A32,#D97706)"}}>
            {supplier.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Fornitore verificato</div>
                <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="supplier-name">{supplier.name}</h1>
              </div>
              {canFav && (
                <Button variant="outline" onClick={toggleFav} disabled={favBusy} className="rounded-full shrink-0" data-testid="favorite-toggle-button">
                  <Heart className={`w-4 h-4 mr-2 ${isFavorite ? "fill-red-500 text-red-500" : ""}`} />
                  {isFavorite ? "Preferito" : "Aggiungi ai preferiti"}
                </Button>
              )}
            </div>
            <div className="mt-3 flex items-center flex-wrap gap-3">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(n => (
                  <Star key={n} className={`w-4 h-4 ${n <= fullStars ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />
                ))}
                <span className="ml-1 font-mono text-sm font-bold text-slate-900" data-testid="supplier-rating">{stars.toFixed(1)}</span>
                <span className="text-xs text-slate-500 font-mono">({supplier.rating_count} {supplier.rating_count === 1 ? "recensione" : "recensioni"})</span>
              </div>
              {supplier.loyalty_stars > 0 && (
                <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-indigo-100" data-testid="loyalty-badge">
                  <Award className="w-3.5 h-3.5 mr-1" />Fedeltà {supplier.loyalty_stars}★
                </Badge>
              )}
              {supplier.total_delivered > 0 && (
                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">
                  <Award className="w-3.5 h-3.5 mr-1" />{supplier.total_delivered} consegne
                </Badge>
              )}
              {supplier.vacation_mode && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100">
                  In ferie
                </Badge>
              )}
            </div>
            {supplier.member_since && (
              <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Iscritto dal {new Date(supplier.member_since).toLocaleDateString("it-IT", {year: "numeric", month: "long"})}</div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Categorie fornite</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {supplier.categories.length === 0 && <span className="text-sm text-slate-500">Nessuna categoria</span>}
            {supplier.categories.map(c => (
              <span key={c.id} className="ga-pill" data-testid={`profile-cat-${c.id}`} data-active="true">{c.label}</span>
            ))}
          </div>
        </div>

        {(supplier.address || supplier.phone) && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {supplier.address && (
              <div className="flex items-start gap-2 text-sm text-slate-700"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />{supplier.address}</div>
            )}
            {supplier.phone && (
              <div className="flex items-start gap-2 text-sm text-slate-700"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />{supplier.phone}</div>
            )}
          </div>
        )}
      </div>

      <div className="mt-8">
        <h2 className="font-display text-2xl font-bold text-slate-900">Recensioni verificate</h2>
        <p className="mt-1 text-sm text-slate-600">Solo da acquirenti che hanno effettivamente ricevuto una consegna.</p>

        <div className="mt-4 space-y-3">
          {supplier.reviews.length === 0 && (
            <div className="ga-card p-8 text-center text-sm text-slate-500">Ancora nessuna recensione.</div>
          )}
          {supplier.reviews.map(r => (
            <div key={r.id} className="ga-card p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{r.rater_name}</div>
                  <div className="text-xs font-mono text-slate-500 mt-0.5">{new Date(r.created_at).toLocaleDateString("it-IT", {day: "2-digit", month: "short", year: "numeric"})}</div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  {[1,2,3,4,5].map(n => (
                    <Star key={n} className={`w-4 h-4 ${n <= Math.round(r.stars) ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />
                  ))}
                </div>
              </div>
              {r.review && <div className="mt-3 text-sm text-slate-700 leading-relaxed">{r.review}</div>}
              <RatingDetails details={r.details} />
              <RatingReply rating={r} supplierName={supplier.name} canReply={false} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
